import { useCallback } from 'react';
import { graphql } from 'react-relay';

import {
	useMutationInterop,
	type MutationFunctionInteropSupportedOptions,
	// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
} from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { expValEquals } from '@confluence/feature-experiments';

import {
	buildMutationContextForNoNetworkError,
	emptyReactionsSummary,
	OPTIMISTIC_SAVE_COMMENT_ID,
} from './commentDataManagerUtils.ts';
import type {
	useCreateInlineReplyMutation$data,
	useCreateInlineReplyMutation$variables,
} from './__generated__/useCreateInlineReplyMutation.graphql.ts';
import { useAllCommentsData, type Comment } from './useAllCommentsData.ts';

// This hook returns a mutation function to create a new inline comment reply. Because the new
// Comments Data Manager caches all the data for comments in Relay, we need to define the mutation
// here so that the cache can be updated fully when a new reply is created - and that means the
// mutation needs to return the full comment data structure that we are using in the returned query
// part of the mutation.  Since that returned query is quite large, we define it here in its own
// hook to avoid having it in many places in the codebase - which makes the code harder to read and
// could lead to the multiple copies getting out of sync.

// Data Mutation Definition:

// Unfortunately our Relay-friendly type generation tools don't support using GQL fragments yet -
// but in order to keep the definition of the comment fields in sync (so the cache update fragment
// is always in sync with the full query) we will need to manually make any changes to one
// definition in any others as well, until our tooling supports fragments and then we move to a
// more fragmented-based approach.  Note there are also comment queries or fragments in the
// following files that need to be kept in sync as well:
// - next/packages/comments-data-manager/src/useAllCommentsData.ts
// - next/packages/comments-data/src/hooks/useCommentById.ts
// - next/packages/comments-data-manager/src/updateCommentInCache.ts
// - next/packages/comments-data-manager/src/useCreateObjectCommentMutation.ts
// - next/packages/comments-data-manager/src/useCreateInlineCommentMutation.ts

const CreateInlineReplyMutation = graphql`
	mutation useCreateInlineReplyMutation(
		$input: ReplyInlineCommentInput!
		$pageId: ID!
		$includeReactionsSummary: Boolean!
	) {
		replyInlineComment(input: $input) {
			id
			author {
				__typename
				displayName
				permissionType
				... on KnownUser {
					accountId
					permissionType
				}
				profilePicture {
					path
				}
				type
			}
			body(representation: ATLAS_DOC_FORMAT) {
				value
			}
			container {
				id
			}
			contentStatus
			createdAt {
				value
			}
			createdAtNonLocalized
			links {
				editui
				webui
			}
			location {
				__typename
				type
				... on FooterComment {
					commentResolveProperties {
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
				}
				... on InlineComment {
					inlineMarkerRef
					inlineResolveProperties {
						isDangling
						resolved
						resolvedFriendlyDate
						resolvedTime
						resolvedUser
					}
					inlineText
				}
			}
			parentId
			permissions {
				isEditable
				isRemovable
				isResolvable
			}
			reactionsSummary(pageId: $pageId, contentType: "page", childType: "comment")
				@include(if: $includeReactionsSummary) {
				ari
				containerAri
				reactionsCount
				reactionsSummaryForEmoji {
					id
					count
					emojiId
					reacted
				}
			}
			# don't need the 'replies' field because new comments are always created without any replies
			version {
				by {
					__typename
					type
					displayName
					... on KnownUser {
						accountId
					}
				}
				friendlyWhen
				number
				when
			}
		}
	}
`;

// NOTE: Ideally, the return type of this function should be `useCreateInlineReplyMutation$data`
// instead of `any`. However, `useCreateInlineReplyMutation$data` type does not define these
// `__typename` fields (because they are not explicitly fetched in the mutation above), but actual
// GQL responses contain them.
// Thus, these `__typename` fields are *required* for Apollo's writeQuery to correctly merge the
// optimistic response comment with comments fetched in actual GraphQL responses.
// The proper fix is to update the mutation above (and all other queries & mutations in this package
// for consistency) to include all the `__typename` fields.
export const getOptimisticResponse = ({
	userId,
	displayName,
	adf,
	pageId,
	parentCommentId,
	parentCommentAnnotationId,
}: {
	userId: string;
	displayName: string;
	adf: object;
	pageId: string;
	parentCommentId: string;
	parentCommentAnnotationId: string;
}): any => {
	const nowISOString = new Date().toISOString();

	return {
		replyInlineComment: {
			author: {
				__typename: 'KnownUser',
				accountId: userId,
				displayName,
				permissionType: null,
				profilePicture: {
					path: userId ? `/wiki/aa-avatar/${userId}` : '',
					__typename: 'Icon',
				},
				type: 'known',
			},
			body: {
				value: JSON.stringify(adf),
				__typename: 'DocumentBody',
			},
			container: {
				id: pageId,
				__typename: 'Content',
			},
			contentStatus: 'current',
			createdAt: {
				value: '',
				__typename: 'ConfluenceDate',
			},
			createdAtNonLocalized: nowISOString, // this is required to correctly sort comments
			id: OPTIMISTIC_SAVE_COMMENT_ID,
			links: {
				editui: null,
				webui: null,
				__typename: 'Map_LinkType_String',
			},
			location: {
				__typename: 'InlineComment',
				type: 'INLINE',
				inlineMarkerRef: parentCommentAnnotationId,
				inlineResolveProperties: {
					isDangling: false,
					resolved: false,
					resolvedFriendlyDate: null,
					resolvedTime: null,
					resolvedUser: null,
					__typename: 'InlineCommentResolveProperties',
				},
				inlineText: '',
			},
			parentId: parentCommentId,
			permissions: {
				isEditable: true,
				isRemovable: true,
				isResolvable: true,
				__typename: 'CommentPermissions',
			},
			version: {
				by: {
					__typename: 'KnownUser',
					accountId: userId,
					displayName,
					type: 'user',
				},
				friendlyWhen: '',
				when: nowISOString, // this is required to show the creation time and tooltip correctly
				number: 1,
				__typename: 'Version',
			},
			__typename: 'Comment',
		},
	};
};

export type CreateInlineReplyInteropOptions = MutationFunctionInteropSupportedOptions<
	useCreateInlineReplyMutation$data,
	Omit<useCreateInlineReplyMutation$variables, 'includeReactionsSummary'>
> & {
	variables: Omit<useCreateInlineReplyMutation$variables, 'includeReactionsSummary'>;
};

export const useCreateInlineReplyMutation = ({
	contentId,
}: {
	contentId: string;
}): {
	createInlineReplyInterop: ({
		options,
		onCacheUpdateError,
		onCacheMiss,
	}: {
		options: CreateInlineReplyInteropOptions;
		onCacheUpdateError: (error: Error) => void;
		onCacheMiss?: (error: Error) => void;
	}) => Promise<{
		data?: useCreateInlineReplyMutation$data | undefined;
	}>;
} => {
	const { addCommentInCache } = useAllCommentsData({
		contentId,
		options: {
			skip: true, // Only using write operations, no need to fetch data
		},
	});
	const [createInlineReplyFn] = useMutationInterop<
		useCreateInlineReplyMutation$data,
		useCreateInlineReplyMutation$variables
	>(CreateInlineReplyMutation, buildMutationContextForNoNetworkError());

	const addEmptyInlineRepliesAndReactions = useCallback(
		(commentData: useCreateInlineReplyMutation$data['replyInlineComment']): Comment => {
			return commentData
				? {
						...commentData,
						contributors: [],
						replies: [],
						reactionsSummary: expValEquals(
							'confluence_fe_use_create_comment_exclude_reactions',
							'isEnabled',
							true,
						)
							? emptyReactionsSummary
							: (commentData.reactionsSummary ?? emptyReactionsSummary),
					}
				: // @ts-expect-error - FIXME: This error was surfaced after enabling the `typescriptExcludeUndefinedFromNullableUnion`
					// Relay compiler option. Previously, `undefined` was incorrectly included in nullable unions in generated types; this option removes it. The owning team should fix this properly.
					undefined;
		},
		[],
	);

	// This is the interop mutation function that will be used by components that need to create
	// inline comment replies via the Comments Data Manager.
	const createInlineReplyInterop = useCallback(
		({
			options,
			onCacheUpdateError,
			onCacheMiss,
		}: {
			options: CreateInlineReplyInteropOptions;
			onCacheUpdateError: (error: Error) => void;
			onCacheMiss?: (error: Error) => void;
		}): Promise<{
			data?: useCreateInlineReplyMutation$data | undefined;
		}> => {
			const updateNewCommentReplyInCache: NonNullable<
				MutationFunctionInteropSupportedOptions<
					useCreateInlineReplyMutation$data,
					useCreateInlineReplyMutation$variables
				>['update']
			> = (_dataProxy, mutationResult) => {
				addCommentInCache({
					newComment: addEmptyInlineRepliesAndReactions(mutationResult.data!.replyInlineComment),
					onError: onCacheUpdateError,
					onCacheMiss,
				});
			};

			return createInlineReplyFn({
				variables: {
					...options.variables,
					includeReactionsSummary: !expValEquals(
						'confluence_fe_use_create_comment_exclude_reactions',
						'isEnabled',
						true,
					),
				},
				update: updateNewCommentReplyInCache,
				optimisticResponse: options.optimisticResponse,
			});
		},
		[addCommentInCache, addEmptyInlineRepliesAndReactions, createInlineReplyFn],
	);

	return { createInlineReplyInterop };
};
